import { createStore, createEvent, createEffect, combine } from 'effector';
import request, { ClientOptions } from '@helpers/request';
import api from '@constants/endpoints';
import createPreloadersStore from './preloaders';
import IPost from '@interfaces/IPost';
import IComment from '@interfaces/IComment';
import { PaginationProps } from 'antd/lib/pagination';
import createPaginationStore from './pagination';
import { get } from 'lodash';
import { AxiosResponse } from 'axios';
import { block, unblock } from '@stores/posts';
import { block as blockComment, unblock as unblockComment } from '@stores/comments';
import update from 'immutability-helper';

const defaultCommentsFilters = {
  sort: 'whenCommented,desc',
  visibleBlockPost: 'ALL',
};

const REPORT_PAGE_SIZE = 1000;

export const reset = createEvent('Post store reset');
export const setPost = createEvent<IPost>('Set Detail Post');

export const getItem = createEffect({
  name: 'getItem',
  async handler(params: { postId?: IPost['id']; clientOptions?: ClientOptions }) {
    const { postId, clientOptions } = params;
    const response = await request(
      api.posts.getItem,
      {
        urlParams: { postId },
      },
      clientOptions,
    );
    return response;
  },
});

export const getComments = createEffect({
  name: 'getComments',
  async handler(params: { postId?: IPost['id']; pagination?: PaginationProps }) {
    const paginationParams: PaginationProps = {
      ...pagination.getState(),
      ...params?.pagination,
    };
    const { pageSize, current } = paginationParams;

    const postId = params.postId ? params.postId : item.getState()?.id;
    const response = await request(api.comments.getListForPost, {
      urlParams: { postId },
      params: {
        paged: true,
        size: pageSize,
        page: (current || 1) - 1,

        ...defaultCommentsFilters,
      },
    });
    return response;
  },
});

export const getLikes = createEffect({
  name: 'getLikes',
  async handler(params: { postId?: IPost['id']; pagination?: PaginationProps }) {
    const paginationParams: PaginationProps = {
      ...pagination.getState(),
      ...params?.pagination,
    };
    const { pageSize, current } = paginationParams;

    const postId = params.postId ? params.postId : item.getState()?.id;
    const response = await request(api.likes.getAccountListForPost, {
      urlParams: { postId },
      params: {
        paged: true,
        size: pageSize,
        page: (current || 1) - 1,
      },
    });
    return response;
  },
});

const commentsBodyPath = ['data'];
const likesBodyPath = ['data', 'body'];
const itemBodyPath = ['data'];

const getAllPages = async <T>(
  getPage: (page: number) => Promise<AxiosResponse>,
  bodyPath: string[],
): Promise<T[]> => {
  const data: T[] = [];
  let page = 0;
  let hasNextPage = true;

  while (hasNextPage) {
    const response = await getPage(page);
    const pageContent = get(response, [...bodyPath, 'content'], []) as T[];
    const isLastPage = get(response, [...bodyPath, 'last']);
    const totalElements = get(response, [...bodyPath, 'totalElements']);
    const totalPages = get(response, [...bodyPath, 'totalPages']);

    data.push(...pageContent);

    if (
      !pageContent.length ||
      isLastPage ||
      (typeof totalElements === 'number' && data.length >= totalElements) ||
      (typeof totalPages === 'number' && page + 1 >= totalPages)
    ) {
      hasNextPage = false;
    } else {
      page += 1;
    }
  }

  return data;
};

const getPostCommentsPages = (postId: IPost['id']) =>
  getAllPages<IComment>(
    (page) =>
      request(api.comments.getListForPost, {
        urlParams: { postId },
        params: {
          paged: true,
          size: REPORT_PAGE_SIZE,
          page,
          ...defaultCommentsFilters,
        },
      }),
    commentsBodyPath,
  );

const getChildCommentsPages = (commentId: IComment['id']) =>
  getAllPages<IComment>(
    (page) =>
      request(api.comments.getListForComment, {
        urlParams: { commentId },
        params: {
          paged: true,
          size: REPORT_PAGE_SIZE,
          page,
          ...defaultCommentsFilters,
        },
      }),
    commentsBodyPath,
  );

const isComment = (value: any): value is IComment =>
  !!value &&
  typeof value === 'object' &&
  !!value.id &&
  !!value.author &&
  typeof value.whenCommented === 'string';

const getEmbeddedChildComments = (comment: IComment): IComment[] => {
  return Object.keys(comment).reduce((children: IComment[], key) => {
    const value = (comment as Record<string, any>)[key];

    if (Array.isArray(value) && value.every(isComment)) {
      return [...children, ...value];
    }

    return children;
  }, []);
};

const addCommentWithChildren = async (
  comment: IComment,
  reportComments: IComment[],
  addedCommentIds: Set<IComment['id']>,
) => {
  if (addedCommentIds.has(comment.id)) {
    return;
  }

  reportComments.push(comment);
  addedCommentIds.add(comment.id);

  const embeddedChildComments = getEmbeddedChildComments(comment);
  const shouldFetchChildComments =
    !!comment.childrenComments && embeddedChildComments.length < comment.childrenComments;
  const childComments = shouldFetchChildComments
    ? [...embeddedChildComments, ...(await getChildCommentsPages(comment.id))]
    : embeddedChildComments;

  for (const childComment of childComments) {
    await addCommentWithChildren(childComment, reportComments, addedCommentIds);
  }
};

export const getReportComments = createEffect({
  name: 'getReportComments',
  async handler(postId: IPost['id']) {
    const postComments = await getPostCommentsPages(postId);
    const reportComments: IComment[] = [];
    const addedCommentIds = new Set<IComment['id']>();

    for (const comment of postComments) {
      await addCommentWithChildren(comment, reportComments, addedCommentIds);
    }

    return reportComments;
  },
});

export const getReportLikes = createEffect({
  name: 'getReportLikes',
  async handler(postId: IPost['id']) {
    return getAllPages<any>(
      (page) =>
        request(api.likes.getAccountListForPost, {
          urlParams: { postId },
          params: {
            paged: true,
            size: REPORT_PAGE_SIZE,
            page,
          },
        }),
      likesBodyPath,
    );
  },
});

export const blockCommentHandler = (
  state: IComment[],
  { result, params }: { result: AxiosResponse; params: { isSubcomment?: boolean } },
) => {
  const updatedComment: IComment = result.data;
  const updatedCommentIndex = state.findIndex((c) => c.id === updatedComment.id);
  if (updatedCommentIndex >= 0) {
    return update(state, {
      $splice: [[updatedCommentIndex, 1, updatedComment]],
    });
  }
  return state;
};

const { pagination } = createPaginationStore({
  getList: getComments,
  listName: 'comments',
  resetEvent: reset,
  bodyPath: commentsBodyPath,
});

const preloaders = createPreloadersStore([
  getItem,
  getComments,
  getLikes,
  getReportComments,
  getReportLikes,
  blockComment,
  unblockComment,
  block,
  unblock,
]);

const item = createStore<IPost | null>(null)
  .on(getItem.done, (state, { result }) => get(result, itemBodyPath, null))
  .on(setPost, (state, post) => post)
  .on(block.done, (state, { result }) => result.data)
  .on(unblock.done, (state, { result }) => result.data)
  .reset(reset);

const comments = createStore<IComment[]>([])
  .on(getComments.done, (state, { result }) => get(result, [...commentsBodyPath, 'content'], []))
  .on(blockComment.done, blockCommentHandler)
  .on(unblockComment.done, blockCommentHandler)
  .reset(reset);

const likes = createStore<any[]>([])
  .on(getLikes.done, (state, { result }) => get(result, [...likesBodyPath, 'content'], []))
  .reset(reset);

const post = combine({
  preloaders,
  item,
  comments,
  likes,
  pagination,
});

export default post;
